import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, CheckCircle2, Clock, Download, FileText, Loader2,
  RefreshCw, Settings, ShieldX, Upload,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { ROLES_LISTAS } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Panel del super_admin para operar el barrido diario centralizado:
 *   1. Exportar la base minimalista de clientes (3 columnas Quién es Quién).
 *   2. Subir el archivo de resultados de Quién es Quién (mismas 3 columnas + 1 columna extra).
 *   3. Configurar el layout de export (encabezados, RFC genérico, delimitador).
 *   4. Ver histórico de mapas de exportación (para saber cuáles ya se reconciliaron).
 */
export default function BarridoDiarioAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("export");
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [entityId, setEntityId] = useState(""); // "" = todas las entidades
  const [entidades, setEntidades] = useState([]);
  const [exports, setExports] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(null); // {mapa_id, fecha_corte}
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [uploadProveedor, setUploadProveedor] = useState("Quién es Quién");
  const [uploadFechaListas, setUploadFechaListas] = useState("");
  const [pendientesInfo, setPendientesInfo] = useState(null);
  const fileInputRef = useRef(null);

  const noAccess = !ROLES_LISTAS.includes(user?.role);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ex, cfg, ents, pend] = await Promise.all([
        api.get("/admin/barrido/exports").then((r) => r.data).catch(() => []),
        api.get("/admin/barrido/config").then((r) => r.data).catch(() => null),
        api.get("/admin/barrido/entidades").then((r) => r.data).catch(() => []),
        api.get("/admin/barrido/pendientes").then((r) => r.data).catch(() => null),
      ]);
      setExports(Array.isArray(ex) ? ex : []);
      setConfig(cfg);
      setEntidades(Array.isArray(ents) ? ents : []);
      setPendientesInfo(pend);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!noAccess) loadAll();
  }, [noAccess]);

  const ejecutarExport = async (formato) => {
    if (!fecha) return toast.error("Selecciona una fecha de corte.");
    setExporting(true);
    try {
      const qs = new URLSearchParams({ fecha, formato });
      if (entityId) qs.set("entity_id", entityId);
      const res = await api.get(`/admin/barrido/export?${qs.toString()}`, { responseType: "blob" });
      const mapaId = res.headers["x-mapa-id"];
      const totalFilas = res.headers["x-total-filas"];
      const totalExcl = res.headers["x-total-excluidos"];
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      // Deriva slug de entidad para nombre local si aplica.
      const ent = entidades.find((e) => e.entity_id === entityId);
      const slug = entityId && ent
        ? "_" + (ent.nombre || entityId).replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase().slice(0, 24)
        : "";
      const a = document.createElement("a");
      a.href = url;
      a.download = `barrido_diario_${fecha}${slug}_mapa-${(mapaId || "").slice(0, 8)}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const alcance = entityId ? `entidad ${ent?.nombre || entityId}` : "todas las entidades";
      toast.success(`Base exportada (${alcance}): ${totalFilas} filas · ${totalExcl} excluidas. Súbela a Quién es Quién y regresa el CSV con la columna de resultado.`);
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  const descargarPlantilla = async () => {
    try {
      const res = await api.get("/admin/barrido/plantilla", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "barrido_plantilla_ejemplo.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada (ejemplo con el layout actual).");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitUpload = async () => {
    if (!uploadDialog || !uploadFile) return toast.error("Selecciona un archivo.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("fecha_corte", uploadDialog.fecha_corte);
      fd.append("mapa_id", uploadDialog.mapa_id);
      fd.append("archivo_resultado", uploadFile);
      fd.append("proveedor", uploadProveedor || "Quién es Quién");
      if (uploadFechaListas) fd.append("fecha_lista_cotejo", uploadFechaListas);
      const res = await api.post("/admin/barrido/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLastResult(res.data);
      const con = res.data.entidades_con_coincidencia;
      const total = res.data.total_entidades;
      const alertas = res.data.alertas_creadas || 0;
      const correos = res.data.correos_enviados || 0;
      toast.success(
        con > 0
          ? `Barrido procesado: ${total} entidades, ${con} con coincidencia. ${alertas} alerta(s) al OC · ${correos} correo(s). Constancias emitidas.`
          : `Barrido procesado: ${total} entidades sin coincidencias. Constancias emitidas.`
      );
      setUploadDialog(null);
      setUploadFile(null);
      setUploadFechaListas("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const guardarConfig = async () => {
    if (!config) return;
    try {
      const res = await api.put("/admin/barrido/config", {
        encabezado_full_name: config.encabezado_full_name,
        encabezado_tax_id: config.encabezado_tax_id,
        encabezado_national_id: config.encabezado_national_id,
        rfc_generico_extranjero: config.rfc_generico_extranjero,
        delimitador: config.delimitador,
        bom_utf8: config.bom_utf8,
      });
      setConfig(res.data);
      toast.success("Layout actualizado.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pendientes = useMemo(() => exports.filter((e) => !e.reconciliado), [exports]);

  if (noAccess) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="barrido-admin-403">
        <ShieldX className="h-10 w-10 text-red-400" />
        <p className="mt-3 text-sm text-slate-500">
          Solo la plataforma FELVORA (Super Administrador o Cargador de Listas) puede operar
          el barrido diario centralizado.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="barrido-admin-page">
      <PageHeader
        title="Barrido diario centralizado"
        subtitle="Canal paralelo manual — Quién es Quién · Operado por Felvora"
      >
        <Button variant="outline" size="sm" onClick={loadAll} data-testid="barrido-refresh">
          <RefreshCw className="mr-1.5 h-4 w-4" /> Refrescar
        </Button>
      </PageHeader>

      <div className="p-6 md:p-8">
        {/* Aviso destacado: exportaciones pendientes sin subir */}
        {pendientesInfo && pendientesInfo.atrasados > 0 && (
          <div
            className="mb-4 flex items-start gap-3 rounded-lg border-2 border-red-400 bg-red-50 px-4 py-3 text-sm text-red-900"
            data-testid="banner-pendientes-atrasados"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="font-bold uppercase tracking-wide">
                {pendientesInfo.atrasados} exportación(es) pendiente(s) de subir
              </p>
              <p className="mt-1 text-red-800">
                Hay corridas exportadas hace más de {pendientesInfo.dias_umbral} días que aún no
                se han reconciliado con el resultado del proveedor. Súbelas cuanto antes o
                marca las que ya no aplican.
              </p>
              <ul className="mt-2 space-y-1 text-xs text-red-800">
                {pendientesInfo.pendientes
                  .filter((p) => p.atrasado)
                  .slice(0, 5)
                  .map((p) => (
                    <li key={p.mapa_id} data-testid={`pendiente-${p.mapa_id}`}>
                      <span className="font-mono">{p.mapa_id?.slice(0, 8)}…</span>
                      <span className="mx-2">·</span>
                      corte <b>{p.fecha_corte}</b>
                      <span className="mx-2">·</span>
                      {p.total_filas} filas
                      <span className="mx-2">·</span>
                      <b>hace {p.dias_desde_exportacion} día(s)</b>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
        {pendientesInfo && pendientesInfo.atrasados === 0 && pendientesInfo.total_pendientes > 0 && (
          <div
            className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900"
            data-testid="banner-pendientes-info"
          >
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
            <div>
              {pendientesInfo.total_pendientes} exportación(es) sin reconciliar (dentro del
              plazo de {pendientesInfo.dias_umbral} días). Recuerda subir los resultados cuando
              el proveedor te devuelva el archivo.
            </div>
          </div>
        )}
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-[13px] text-blue-900">
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <div>
            <span className="font-semibold">Flujo:</span> {" "}
            (1) exporta la base con las 3 columnas configuradas; (2) pégala en Quién es Quién (consulta masiva);
            (3) descarga el archivo con la columna de resultado; (4) súbelo aquí — Felvora reconcilia por número de fila y emite la constancia a cada entidad.
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="export" data-testid="tab-export"><Download className="mr-1.5 h-3.5 w-3.5" /> Exportar</TabsTrigger>
            <TabsTrigger value="upload" data-testid="tab-upload"><Upload className="mr-1.5 h-3.5 w-3.5" /> Subir resultados</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config"><Settings className="mr-1.5 h-3.5 w-3.5" /> Layout</TabsTrigger>
          </TabsList>

          {/* -------- EXPORT -------- */}
          <TabsContent value="export" className="mt-4">
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Nueva corrida — descargar base para Quién es Quién</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:items-end">
                <div>
                  <Label className="text-[11px] text-slate-500">Fecha de corte</Label>
                  <Input data-testid="barrido-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="h-9" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Alcance</Label>
                  <select
                    data-testid="barrido-entity-select"
                    className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm"
                    value={entityId}
                    onChange={(e) => setEntityId(e.target.value)}
                  >
                    <option value="">Todas las entidades</option>
                    {entidades.map((e) => (
                      <option key={e.entity_id} value={e.entity_id}>
                        {e.nombre} ({e.conteo_clientes})
                      </option>
                    ))}
                  </select>
                </div>
                <Button data-testid="barrido-export-csv" onClick={() => ejecutarExport("csv")} disabled={exporting || !fecha} className="bg-primary hover:bg-sidebar-hover">
                  {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Exportar CSV
                </Button>
                <Button variant="outline" data-testid="barrido-export-xlsx" onClick={() => ejecutarExport("xlsx")} disabled={exporting || !fecha}>
                  <Download className="mr-1.5 h-4 w-4" /> Exportar XLSX
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <button className="underline hover:text-primary" onClick={descargarPlantilla} data-testid="barrido-plantilla">
                  Descargar plantilla de ejemplo (verificar layout antes del barrido real)
                </button>
                {entityId ? (
                  <Badge className="bg-blue-100 text-[11px] text-blue-800">
                    Modo: 1 sola entidad · {entidades.find((e) => e.entity_id === entityId)?.conteo_clientes || 0} clientes esperados
                  </Badge>
                ) : (
                  <Badge className="bg-slate-100 text-[11px] text-slate-700">
                    Modo: TODAS · {entidades.reduce((s, e) => s + e.conteo_clientes, 0)} clientes esperados
                  </Badge>
                )}
              </div>
              {config && (
                <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
                  <Badge className="bg-slate-100 text-slate-700">Layout activo: {config.encabezado_full_name}, {config.encabezado_tax_id}, {config.encabezado_national_id}</Badge>
                  <Badge className="bg-slate-100 text-slate-700">Genérico extranjero: {config.rfc_generico_extranjero}</Badge>
                  <Badge className="bg-slate-100 text-slate-700">Delim: {config.delimitador === "\t" ? "TAB" : config.delimitador}</Badge>
                  {config.bom_utf8 && <Badge className="bg-slate-100 text-slate-700">BOM UTF-8</Badge>}
                </div>
              )}
            </div>

            <h3 className="mb-2 mt-6 text-sm font-semibold text-slate-700">Historial de exportaciones</h3>
            <TablaMapas exports={exports} onSubir={(m) => { setUploadDialog(m); setTab("upload"); }} />
          </TabsContent>

          {/* -------- UPLOAD -------- */}
          <TabsContent value="upload" className="mt-4">
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="mb-1 text-sm font-semibold text-slate-700">Subir resultado de Quién es Quién</h3>
              <p className="mb-4 text-xs text-slate-500">
                Selecciona el mapa/exportación al que corresponde el archivo. El sistema valida fila-por-fila y ancla cada coincidencia al cliente exacto (número de fila del mapa, NO por RFC).
              </p>
              {pendientes.length === 0 ? (
                <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 py-8 text-center text-sm text-slate-500">
                  No hay exportaciones pendientes. Genera una en la pestaña &quot;Exportar&quot;.
                </div>
              ) : (
                <div className="space-y-2" data-testid="mapas-pendientes">
                  {pendientes.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setUploadDialog({ mapa_id: m.id, fecha_corte: m.fecha_corte, total_filas: m.total_filas })}
                      data-testid={`btn-subir-${m.id.slice(0, 8)}`}
                      className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-primary hover:bg-slate-50"
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-700">Corte del {m.fecha_corte}</div>
                        <div className="text-xs text-slate-500">
                          Mapa {m.id.slice(0, 8)}… · {m.total_filas} filas · {m.total_excluidos} excluidas · {new Date(m.exportado_at).toLocaleString("es-MX")}
                        </div>
                      </div>
                      <Upload className="h-4 w-4 text-slate-400" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {lastResult && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="upload-result">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-900">
                  <CheckCircle2 className="h-4 w-4" /> Barrido {lastResult.fecha_corte} procesado
                </div>
                <div className="text-xs text-emerald-900">
                  {lastResult.total_entidades} entidad(es) · {lastResult.entidades_con_coincidencia} con coincidencia(s) · Integridad {lastResult.integridad_alterada ? "alterada" : "íntegra"}
                </div>
                <div className="mt-2 text-[11px] text-emerald-800">
                  Cada entidad ya puede ver su constancia PDF en el módulo PLD/CNBV → Screening.
                </div>
              </div>
            )}
          </TabsContent>

          {/* -------- CONFIG -------- */}
          <TabsContent value="config" className="mt-4">
            {config && (
              <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="barrido-config-panel">
                <h3 className="mb-1 text-sm font-semibold text-slate-700">Layout del archivo de exportación</h3>
                <p className="mb-4 text-xs text-slate-500">
                  Ajusta los encabezados si Quién es Quién cambia el formato de &quot;consulta masiva&quot;. Los cambios se aplican al próximo export y también al validador del upload.
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <Label className="text-[11px] text-slate-500">Encabezado columna 1 (nombre)</Label>
                    <Input data-testid="cfg-h1" value={config.encabezado_full_name} onChange={(e) => setConfig({ ...config, encabezado_full_name: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Encabezado columna 2 (RFC)</Label>
                    <Input data-testid="cfg-h2" value={config.encabezado_tax_id} onChange={(e) => setConfig({ ...config, encabezado_tax_id: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Encabezado columna 3 (CURP)</Label>
                    <Input data-testid="cfg-h3" value={config.encabezado_national_id} onChange={(e) => setConfig({ ...config, encabezado_national_id: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">RFC genérico de extranjeros</Label>
                    <Input data-testid="cfg-generico" value={config.rfc_generico_extranjero} onChange={(e) => setConfig({ ...config, rfc_generico_extranjero: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] text-slate-500">Delimitador</Label>
                    <select data-testid="cfg-delim" className="h-10 w-full rounded-md border border-slate-200 px-2" value={config.delimitador} onChange={(e) => setConfig({ ...config, delimitador: e.target.value })}>
                      <option value=",">Coma (,)</option>
                      <option value=";">Punto y coma (;)</option>
                      <option value="|">Barra vertical (|)</option>
                      <option value="\t">Tabulador</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm">
                      <input data-testid="cfg-bom" type="checkbox" checked={!!config.bom_utf8} onChange={(e) => setConfig({ ...config, bom_utf8: e.target.checked })} />
                      Incluir BOM UTF-8
                    </label>
                  </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" onClick={descargarPlantilla} data-testid="cfg-plantilla">
                    <Download className="mr-1.5 h-4 w-4" /> Descargar plantilla
                  </Button>
                  <Button onClick={guardarConfig} data-testid="cfg-guardar" className="bg-primary hover:bg-sidebar-hover">
                    Guardar layout
                  </Button>
                </div>
                {config.actualizado_at && (
                  <p className="mt-3 text-[11px] text-slate-400">
                    Última edición: {config.actualizado_por} · {new Date(config.actualizado_at).toLocaleString("es-MX")}
                  </p>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Dialog upload */}
        <Dialog open={!!uploadDialog} onOpenChange={(v) => { if (!v) { setUploadDialog(null); setUploadFile(null); } }}>
          <DialogContent data-testid="dialog-upload">
            <DialogHeader>
              <DialogTitle>Subir resultado del corte {uploadDialog?.fecha_corte}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Mapa: <span className="font-mono">{uploadDialog?.mapa_id?.slice(0, 8)}…</span> · {uploadDialog?.total_filas} filas
              </p>
              <div>
                <Label className="text-[11px] text-slate-500">Archivo de resultado (CSV con las 3 columnas + 1 columna extra)</Label>
                <input
                  ref={fileInputRef}
                  data-testid="upload-file"
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-white hover:file:bg-sidebar-hover"
                />
              </div>
              <div className="rounded-md bg-amber-50 p-3 text-[11px] text-amber-900">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                El archivo debe conservar el orden y filas del export original. Si Quién es Quién agregó una columna al final con el resultado, el sistema la interpreta automáticamente.
              </div>
              {/* Trazabilidad regulatoria — proveedor + fecha de corte de listas */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-[11px] text-slate-500">Proveedor externo</Label>
                  <Input
                    data-testid="upload-proveedor"
                    value={uploadProveedor}
                    onChange={(e) => setUploadProveedor(e.target.value)}
                    placeholder="Quién es Quién"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-500">Fecha de corte de las listas consultadas</Label>
                  <Input
                    data-testid="upload-fecha-listas"
                    type="date"
                    value={uploadFechaListas}
                    onChange={(e) => setUploadFechaListas(e.target.value)}
                    className="mt-1"
                  />
                  <p className="mt-1 text-[10px] text-slate-400">
                    Fecha declarada por el proveedor. Queda en evidencia para CNBV.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setUploadDialog(null); setUploadFile(null); }} data-testid="dialog-upload-cancelar">Cancelar</Button>
              <Button onClick={submitUpload} disabled={!uploadFile || uploading} data-testid="dialog-upload-confirmar" className="bg-primary hover:bg-sidebar-hover">
                {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Procesar barrido
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function TablaMapas({ exports, onSubir }) {
  if (!exports?.length) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 py-8 text-center text-sm text-slate-500">
        Sin exportaciones aún.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="tabla-mapas">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fecha corte</TableHead>
            <TableHead>Mapa</TableHead>
            <TableHead>Filas</TableHead>
            <TableHead>Excluidas</TableHead>
            <TableHead>Exportado</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acción</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {exports.map((m) => (
            <TableRow key={m.id} data-testid={`row-mapa-${m.id.slice(0, 8)}`}>
              <TableCell className="text-sm">{m.fecha_corte}</TableCell>
              <TableCell className="font-mono text-xs">{m.id.slice(0, 8)}…</TableCell>
              <TableCell className="tabular-nums">{m.total_filas}</TableCell>
              <TableCell className="tabular-nums text-slate-500">{m.total_excluidos}</TableCell>
              <TableCell className="text-xs text-slate-500">{new Date(m.exportado_at).toLocaleString("es-MX")}</TableCell>
              <TableCell>
                {m.reconciliado ? (
                  <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" /> Reconciliado</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700"><Clock className="mr-1 h-3 w-3" /> Pendiente</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                {!m.reconciliado && (
                  <Button size="sm" variant="outline" onClick={() => onSubir({ mapa_id: m.id, fecha_corte: m.fecha_corte, total_filas: m.total_filas })} data-testid={`subir-mapa-${m.id.slice(0, 8)}`}>
                    <Upload className="mr-1 h-3.5 w-3.5" /> Subir
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
